import i18n from '@/i18n'
import { dockageDeck, truckAccount } from '@/images/AngelSvgLibrary'
import IconHelpers from '@/utils/IconHelpers'

export class EnumValue {
  constructor (value, label) {
    this.label = label
    this.value = value
    Object.freeze(this)
  }

  get name () {
    return i18n.t(this.label)
  }
}

export class EnumValueWithIcon {
  constructor (value, label, icon) {
    this.label = label
    this.value = value
    this.icon = icon
    Object.freeze(this)
  }

  get name () {
    return i18n.t(this.label)
  }
}

export class EnumValueWithDetails {
  constructor (value, label, details) {
    this.label = label
    this.value = value
    this.details = details
    Object.freeze(this)
  }

  get name () {
    return i18n.t(this.label)
  }
}

export class EnumValueWithExplanation {
  constructor (value, label, explanation) {
    this.label = label
    this.value = value
    this.explanation = explanation
    Object.freeze(this)
  }

  get name () {
    return i18n.t(this.label)
  }

  get definition () {
    return i18n.t(this.explanation)
  }
}

export class Enum {
  constructor () {
    throw Error('cannot instantiate enum base class')
  }

  static get enums () {
    return Object.values(this).filter(v => v instanceof EnumValue || v instanceof EnumValueWithIcon || v instanceof EnumValueWithExplanation || v instanceof EnumValueWithDetails)
  }

  static get names () {
    return this.enums.map(e => e.name)
  }

  // int -> str
  static fromInt (i) {
    return this.forInt(i)?.name ?? i18n.t('notAvailable')
  }

  // int -> str
  static labelFromInt (i) {
    return this.forInt(i)?.label ?? 'notAvailable'
  }

  // str -> int
  static toInt (s) {
    return this.forName(s)?.value ?? -1
  }

  static forInt (i) {
    return this.enums.find(e => e.value === i)
  }

  static forLabel (s) {
    return this.enums.find(e => e.label === s)
  }

  static forName (s) {
    return this.enums.find(e => e.name === s)
  }
}

export class ContractStatus extends Enum {
  static Open = new EnumValue(0, 'open')
  static Closed = new EnumValue(1, 'closed')
  static OnHold = new EnumValue(2, 'onHold')
  static Expired = new EnumValue(3, 'expired')
}

export class RecoveryType extends Enum {
  static FlatRate = new EnumValue(0, 'flatRate')
  static PercentageOfPayable = new EnumValue(1, 'percentageOfPayable')
  static RatePerTon = new EnumValue(2, 'ratePerTon')
  static RatePerMBF = new EnumValue(3, 'ratePerMBFVerbatim')
}

export class ContractStatusReason extends Enum {
  static None = new EnumValue(0, 'none')
  static NoAccountsPayableActivitiesDefined = new EnumValue(1, 'noAccountsPayableActivitiesDefined')
  static NoAccountsReceivableActivitiesDefined = new EnumValue(2, 'noAccountsReceivableActivitiesDefined')
  static NoProductSpecified = new EnumValue(3, 'noProductsSpecified')
  static NoActivitiesDefined = new EnumValue(4, 'noActivitiesDefined')
  static WaitingForEffectiveDate = new EnumValue(5, 'waitingForEffectiveDate')
  static NoOpenDecksAtDestination = new EnumValue(6, 'noOpenDecksAtDestination')
  static ExpiredInsurance = new EnumValue(7, 'expiredInsurance')
  static ClosedByTract = new EnumValue(8, 'closedByTract')
  static OpenedByTract = new EnumValue(9, 'openedByTract')
  static MissingTransferActivity = new EnumValue(10, 'missingTransferActivity')
  static NoProductsSpecified = new EnumValue(11, 'noProductsSpecified')
  static CounterpartyNotSet = new EnumValue(12, 'counterpartyNotSet')
  static MissingDepletionActivity = new EnumValue(13, 'missingDepletionActivity')
  static NoDecksWithInventoryAtLocation = new EnumValue(14, 'noDecksWithInventoryAtLocation')
  static FromAccountReceiverLocationNotSet = new EnumValue(15, 'fromAccountReceiverLocationNotSet')
  static SinglePenaltyAP = new EnumValue(16, 'onlyAPActivityIsPenalty')
  static DestAccountReceiverLocationNotSet = new EnumValue(17, 'destAccountReceiverLocationNotSet')
  static MissingContractProducts = new EnumValue(18, 'missingContractProducts')
  static RateSheetMissingOnPayByVolume = new EnumValue(19, 'rateSheetMissingOnPayByVolume')
  static RateSheetNotInEffectOnPayByVolume = new EnumValue(20, 'rateSheetNotInEffectOnPayByVolume')
  static NoScalingRegionOnDestinationAccount = new EnumValue(21, 'noScalingRegionOnDestinationAccount')
}

export class TicketStatus extends Enum {
  static InTransit = new EnumValue(0, 'inTransit')
  static WeighedIn = new EnumValue(1, 'weighedIn')
  static WeighedOut = new EnumValue(2, 'weighedOut')
  static Scaling = new EnumValue(3, 'scaling')
  static PendingValidation = new EnumValue(4, 'pendingValidation')
  static ReadyForPayment = new EnumValue(5, 'readyForPayment')
  static Posted = new EnumValue(6, 'posted')
  static Exported = new EnumValue(7, 'exported')
  static Recalculating = new EnumValue(50, 'recalculating')
  static VolumeComputation = new EnumValue(51, 'volumeComputation')
  static Exception = new EnumValue(99, 'exception')
}

export class DeckStatus extends Enum {
  static Open = new EnumValue(0, 'open')
  static Closed = new EnumValue(1, 'closed')
  static Archived = new EnumValue(2, 'archived')
}

export class ContractType extends Enum {
  static Production = new EnumValue(0, 'production')
  static WoodsSale = new EnumValue(1, 'woodsSale')
  static LogYardSale = new EnumValue(2, 'logYardSale')
  static ByproductSale = new EnumValue(3, 'byproductSale')
  static Transfer = new EnumValue(4, 'transfer')
  static ByproductPurchase = new EnumValue(5, 'byproductPurchase')
  static ByproductHauling = new EnumValue(6, 'byproductHauling')
}

export class PayPeriodStatus extends Enum {
  static Closed = new EnumValue(0, 'closed')
  static Open = new EnumValue(1, 'open')
  static Inactive = new EnumValue(2, 'inactive')
  static Exported = new EnumValue(3, 'exported')
}

export class FiscalYearStatus extends Enum {
  static Closed = new EnumValue(0, 'closed')
  static Open = new EnumValue(1, 'open')
  static Inactive = new EnumValue(2, 'inactive')
}

export class CostType extends Enum {
  static Unspecified = new EnumValue(0, 'unspecified')
  static Production = new EnumValue(1, 'production')
  static Stumpage = new EnumValue(2, 'stumpage')
  static NonInventory = new EnumValue(3, 'nonInventory')
}

export class ProductionCostType extends Enum {
  static Other = new EnumValueWithIcon(0, 'unspecified', undefined)
  static Logging = new EnumValueWithIcon(1, 'logging', 'mdi-axe')
  static Hauling = new EnumValueWithIcon(2, 'hauling', 'mdi-truck')
}

export class TemplateCostType extends Enum {
  static Unspecified = new EnumValue(0, 'unspecified')
  static ProductionCost = new EnumValue(1, 'productionCost')
  static StumpageCost = new EnumValue(2, 'stumpageCost')
  static nonInventoryCost = new EnumValue(3, 'nonInventoryCost')
}

export class ActivityStatus extends Enum {
  static Active = new EnumValue(0, 'active')
  static Inactive = new EnumValue(1, 'inactive')
}

export class PayOn extends Enum {
  static Gross = new EnumValue(0, 'gross')
  static Net = new EnumValue(1, 'net')
  static Defect = new EnumValue(2, 'defect')
  static NatureDefect = new EnumValue(3, 'natureDefect')
  static MismanufacturedDefect = new EnumValue(4, 'mismanufacturedDefect')
  static NotApplicable = new EnumValue(50, 'notAvailable')
}

export class PayBy extends Enum {
  static Load = new EnumValue(2, 'load')
  static Weight = new EnumValue(1, 'payByWeightTons')
  static MetricWeight = new EnumValue(3, 'payByWeightMetricTons')
  static Volume = new EnumValue(0, 'volume')
}

export class AccountingCategory extends Enum {
  static Payable = new EnumValue(0, 'payable')
  static Receivable = new EnumValue(1, 'receivable')
  static Accrual = new EnumValue(2, 'accrual')
}

export class TemplateAccountingCategory extends Enum {
  static AccountsPayable = new EnumValue(0, 'accountsPayable')
  static AccountsReceivable = new EnumValue(1, 'accountsReceivable')
  static Accruals = new EnumValue(2, 'accruals')
}

export class ActivityModifier extends Enum {
  static None = new EnumValue(0, 'none')
  static DistanceTraveled = new EnumValue(1, 'distanceTraveled')
}

export class TagArea extends Enum {
  static Accounts = new EnumValue(0, 'accounts')
  static LoggingAccessibility = new EnumValue(1, 'loggingAccessibility')
}

export class TagFilterMode extends Enum {
  static Any = new EnumValueWithDetails(0, 'anyTag',
    {
      icon: 'mdi-filter-outline',
      description: 'anyTagDescription'
    })

  static All = new EnumValueWithDetails(1, 'allTags',
    {
      icon: 'mdi-filter',
      description: 'allTagsDescription'
    })
}

export class TicketOrigin extends Enum {
  static Loader = new EnumValueWithIcon(0, 'loader', 'loader')
  static WebClient = new EnumValueWithIcon(1, 'office', 'mdi-laptop')
  static Receiver = new EnumValueWithIcon(2, 'receiver', 'receiver')
  static YardOperator = new EnumValueWithIcon(3, 'yardOperator', 'yard-operator')
  static Transporter = new EnumValueWithIcon(4, 'transporter', 'transporter')
  static Kiosk = new EnumValueWithIcon(5, 'angelKiosk', 'kiosk')
}

export const TicketStatusIcon = (ticketStatus,
  transitStatus = TransitStatus.NotSet.value,
  vehicleType = VehicleType.Trailer.value,
  scalingStatus = ScalingStatus.NotSet.value) => {
  const status = {
    icon: '',
    iconColor: 'grey',
    tooltipText: ''
  }

  switch (ticketStatus) {
    case TicketStatus.InTransit.value:
      status.icon = vehicleType === VehicleType.Trailer.value ? TransitStatus.forInt(transitStatus).details.icon : 'train-car-full-logs'
      status.iconColor = TransitStatus.forInt(transitStatus).details.color
      status.tooltipText = i18n.t('inTransit').concat(TransitStatus.NotSet.value !== transitStatus ? ` \u2014 ${TransitStatus.forInt(transitStatus).name}` : '')
      break
    case TicketStatus.WeighedIn.value:
      status.icon = 'mdi-weight'
      status.iconColor = 'grey'
      status.tooltipText = TicketStatus.WeighedIn.name
      break
    case TicketStatus.WeighedOut.value:
      status.icon = 'mdi-receipt-text-clock-outline'
      status.color = 'black'
      status.tooltipText = TicketStatus.WeighedOut.name
      status.iconColor = 'black'
      break
    case TicketStatus.Scaling.value:
      status.icon = ScalingStatus.forInt(scalingStatus).details.icon
      status.iconColor = ScalingStatus.forInt(scalingStatus).details.color
      status.tooltipText = i18n.t('scaling').concat(ScalingStatus.NotSet.value !== scalingStatus ? ` \u2014 ${ScalingStatus.forInt(scalingStatus).name}` : '')
      break
    case TicketStatus.PendingValidation.value:
      status.iconColor = 'grey'
      status.icon = 'mdi-clock-outline'
      status.tooltipText = TicketStatus.PendingValidation.name
      break
    case TicketStatus.ReadyForPayment.value:
      status.icon = 'mdi-currency-usd'
      status.tooltipText = TicketStatus.ReadyForPayment.name
      status.iconColor = 'success'
      break
    case TicketStatus.Posted.value:
      status.icon = 'mdi-checkbox-marked-circle'
      status.tooltipText = TicketStatus.Posted.name
      status.iconColor = 'black'
      break
    case TicketStatus.Exported.value:
      status.icon = 'mdi-checkbox-marked-circle'
      status.tooltipText = TicketStatus.Exported.name
      status.iconColor = 'success'
      break
    case TicketStatus.Recalculating.value:
      status.icon = 'mdi-calculator'
      status.tooltipText = TicketStatus.Recalculating.name
      status.iconColor = 'grey'
      break
    case TicketStatus.VolumeComputation.value:
      status.icon = 'mdi-calculator'
      status.tooltipText = TicketStatus.VolumeComputation.name
      status.iconColor = 'grey'
      break
    default:
      status.icon = 'mdi-exclamation'
      status.tooltipText = TicketStatus.Exception.name
      status.iconColor = 'error'
  }

  return status
}

export class TicketBookStatus extends Enum {
  static Available = new EnumValue(0, 'available')
  static InUse = new EnumValue(1, 'inUse')
  static Inactive = new EnumValue(2, 'archived')
}

export class WoodType extends Enum {
  static Softwood = new EnumValue(0, 'softwood')
  static Hardwood = new EnumValue(1, 'hardwood')
}

export class TimberType extends Enum {
  static Pulpwood = new EnumValue(0, 'pulpwood')
  static ChipNSaw = new EnumValue(1, 'chipnsaw')
  static Sawtimber = new EnumValue(2, 'sawtimber')
  static Poles = new EnumValue(3, 'poles')
  static Posts = new EnumValue(4, 'posts')
  static FuelwoodChips = new EnumValue(5, 'fuelwoodChips')
  static FuelwoodFirewood = new EnumValue(6, 'fuelwoodFirewood')
}

export class TractTypeCategory extends Enum {
  static Stumpage = new EnumValueWithIcon(0, 'stumpage', 'mdi-alpha-s-circle-outline')
  static Delivered = new EnumValueWithIcon(1, 'delivered', 'mdi-alpha-d-circle-outline')
}

export class Outcome extends Enum {
  static Won = new EnumValue(0, 'won')
  static Lost = new EnumValue(1, 'lost')
}

export class RfbStatus extends Enum {
  static Submitted = new EnumValue(0, 'submitted')
  static Complete = new EnumValue(1, 'complete')
  static Withdrawn = new EnumValue(2, 'withdrawn')
  static Pass = new EnumValue(3, 'pass')
}

export class BidProductStatus extends Enum {
  static None = new EnumValue(0, 'none')
  static Won = new EnumValue(1, 'won')
  static Lost = new EnumValue(2, 'lost')
}

export class TractBidState extends Enum {
  static Open = new EnumValue(0, 'open')
  static Won = new EnumValue(1, 'won')
  static Lost = new EnumValue(2, 'lost')
}

export class DefectCategory extends Enum {
  static Mismanufactured = new EnumValue(0, 'mismanufactured')
  static Nature = new EnumValue(1, 'nature')
}

export class RecoveryMethods extends Enum {
  static AutoCalculated = new EnumValue(0, 'autoCalculated')
  static ManualPayment = new EnumValue(1, 'manualPayment')
  static RecurringPayment = new EnumValue(2, 'recurringPayment')
}

export class AdvanceModificationTypes extends Enum {
  static BalanceChange = new EnumValue(0, 'balanceChange')
  static Payoff = new EnumValue(1, 'payoff')
}

export class DefectCollectionType extends Enum {
  static Simple = new EnumValue(0, 'simple')
  static Itemized = new EnumValue(1, 'itemized')
  static Segmented = new EnumValue(2, 'segmented')
}
export class CorrectionType extends Enum {
  static SpecifyNewTicketValues = new EnumValue(0, 'specifyNewValues')
  static RecalculateWithNewContractValues = new EnumValue(1, 'recalculateWithNewContractValues')
  static DeleteTicket = new EnumValue(2, 'deleteTicket')
}

export class CorrectionStatus extends Enum {
  static ReadyForExport = new EnumValue(0, 'readyForExport')
  static Exported = new EnumValue(1, 'exported')
}

export class CertificationCategory extends Enum {
  static Tract = new EnumValue(0, 'tract')
  static Account = new EnumValue(1, 'account')
}

export class RuntimeCounterparty extends Enum {
  static Landowner = new EnumValue(0, 'tractLandowner')
  static DefaultLogger = new EnumValue(1, 'tractDefaultLogger')
  static ContractAccount = new EnumValue(2, 'contractAccount')
  static Hauler = new EnumValue(3, 'tractHauler')
  static Supplier = new EnumValue(4, 'tractSupplier')
  static DestinationAccount = new EnumValue(5, 'destinationAccount')
  static ConsultingForester = new EnumValue(6, 'tractConsultingForester')
  static Driver = new EnumValue(7, 'ticketDriver')
}

export class ConsumptionMode extends Enum {
  static TicketBased = new EnumValue(0, 'ticket')
  static WeightBased = new EnumValue(1, 'weight')
  static BoardFoot = new EnumValue(2, 'boardFoot')
}

export class ReceiverScaleConnectionTypes extends Enum {
  static HTTP = new EnumValue(0, 'http')
  static TCP = new EnumValue(1, 'tcp')
  static Custom = new EnumValue(2, 'custom')
}

export class ReceiverLoadCreationTypes extends Enum {
  static LogsOnly = new EnumValue(0, 'logsOnly')
  static LogsAndByproducts = new EnumValue(1, 'logsAndByproducts')
  static ByproductsOnly = new EnumValue(2, 'byproductsOnly')
}

export class ReceiverLogContractFilterTypes extends Enum {
  static Account = new EnumValue(0, 'account')
  static Tract = new EnumValue(1, 'tract')
}

export class ReceiverByproductSaleContractFilterTypes extends Enum {
  static Account = new EnumValue(0, 'account')
  static Destination = new EnumValue(1, 'destination')
}

export class ReceiverByproductPurchaseContractFilterTypes extends Enum {
  static FromAccountTract = new EnumValue(0, 'fromAccount')
  static Account = new EnumValue(1, 'account')
}

export class ReceiverLogContractLoadCountTimeFilter extends Enum {
  static Day = new EnumValue(0, 'day')
  static Week = new EnumValue(1, 'week')
}

export class ReceiverDefectCollectionBehavior extends Enum {
  static Inbound = new EnumValue(0, 'inbound')
  static Outbound = new EnumValue(1, 'outbound')
  static Unloader = new EnumValue(2, 'unloader')
}

export class ReceiverDefectCollectionUnit extends Enum {
  static Pounds = new EnumValue(0, 'lb')
  static Percentage = new EnumValue(1, 'percentage')
  static Itemized = new EnumValue(2, 'itemized')
  static Segmented = new EnumValue(3, 'segmented')
}

export class SystemDayOfWeek extends Enum {
  static Sunday = new EnumValue(0, 'sunday')
  static Monday = new EnumValue(1, 'monday')
  static Tuesday = new EnumValue(2, 'tuesday')
  static Wednesday = new EnumValue(3, 'wednesday')
  static Thursday = new EnumValue(4, 'thursday')
  static Friday = new EnumValue(5, 'friday')
  static Saturday = new EnumValue(6, 'saturday')
}

export class TicketCorrectionSide extends Enum {
  static Negation = new EnumValue(0, 'negationOfOldValues')
  static NewValues = new EnumValue(1, 'newValues')
}

export const CorrectionSide = [
  {
    name: 'Negation of old values',
    value: 0
  },
  {
    name: 'New values',
    value: 1
  }
]

export class TractStatus extends Enum {
  static Initiated = new EnumValueWithDetails(0, 'initiated',
    {
      icon: 'mdi-pencil-plus-outline',
      color: 'secondary'
    }
  )

  static Cruised = new EnumValueWithDetails(1, 'cruised',
    {
      icon: 'mdi-forest',
      color: 'success'
    }
  )

  static Executed = new EnumValueWithDetails(2, 'executed',
    {
      icon: 'mdi-check-circle-outline',
      color: 'grey'
    }
  )

  static Active = new EnumValueWithDetails(3, 'active',
    {
      icon: 'mdi-check-circle',
      color: 'success'
    }
  )

  static Closed = new EnumValueWithDetails(4, 'closed',
    {
      icon: 'mdi-close-circle',
      color: 'error'
    }
  )

  static Archived = new EnumValueWithDetails(5, 'archived',
    {
      icon: 'mdi-archive',
      color: 'grey'
    }
  )
}

export class EudrBatchStatus extends Enum {
  static Pending = new EnumValueWithDetails(0, 'pending',
    {
      icon: 'mdi-clock-outline',
      color: 'grey'
    }
  )

  static Processing = new EnumValueWithDetails(1, 'processing',
    {
      icon: 'mdi-progress-clock',
      color: 'primary'
    }
  )

  static Complete = new EnumValueWithDetails(2, 'complete',
    {
      icon: 'mdi-check-circle',
      color: 'success'
    }
  )

  static Error = new EnumValueWithDetails(3, 'error',
    {
      icon: 'mdi-close-circle',
      color: 'error'
    }
  )

  static NeedsValidation = new EnumValueWithDetails(4, 'eudrStatusNeedsValidation',
    {
      icon: 'mdi-alert-circle',
      color: 'warning'
    }
  )

  static Validated = new EnumValueWithDetails(5, 'eudrStatusValidated',
    {
      icon: 'mdi-check-decagram',
      color: 'success'
    }
  )

  static Finalized = new EnumValueWithDetails(6, 'eudrStatusFinalized',
    {
      icon: 'mdi-lock-check',
      color: 'success'
    }
  )
}

export class EudrBatchKind extends Enum {
  static Uploaded = new EnumValueWithIcon(0, 'eudrKindUploaded', 'mdi-paperclip')
  static Composite = new EnumValueWithIcon(1, 'eudrKindComposite', 'mdi-layers-triple')
}

export class ContractMode extends Enum {
  static Logs = new EnumValueWithDetails(0, 'logs',
    {
      icon: 'mdi-clipboard-outline',
      color: 'primary',
      canRecover: true
    })

  static Byproducts = new EnumValueWithDetails(1, 'byproducts',
    {
      icon: 'mdi-circular-saw',
      color: 'byproduct',
      canRecover: true
    })

  static Transfers = new EnumValueWithDetails(2, 'transfers',
    {
      icon: 'mdi-transfer',
      color: 'grey',
      canRecover: false
    })

  static LogYardSale = new EnumValueWithDetails(3, 'logYardSale',
    {
      icon: 'mdi-truck-delivery-outline',
      color: 'logyardsale',
      canRecover: false
    })
}

export const CorrectionTypes = [
  {
    name: 'Recalculate with new contract values',
    value: 1
  },
  {
    name: 'Specify new ticket values',
    value: 0
  },
  {
    name: 'Delete ticket',
    value: 2
  }
]

export class IntegrationSourceType extends Enum {
  static Logs = new EnumValue(0, 'logs')
  static Byproducts = new EnumValue(1, 'byproducts')
  static TractPayments = new EnumValue(2, 'tractPayments')
  static Advances = new EnumValue(3, 'advances')
}

export class IntegrationPolarity extends Enum {
  static Negative = new EnumValue(0, 'negative')
  static Positive = new EnumValue(1, 'positive')
}

export class IntegrationStatus extends Enum {
  static NotStarted = new EnumValue(0, 'notStarted')
  static Complete = new EnumValue(1, 'complete')
  static Error = new EnumValue(2, 'error')
  static InProgress = new EnumValue(3, 'inProgress')
}

export class AuditEntryOpType extends Enum {
  static Create = new EnumValue(0, 'create')
  static Update = new EnumValue(1, 'update')
  static Delete = new EnumValue(2, 'delete')
}

export class AddressType extends Enum {
  static ShipTo = new EnumValue(0, 'shipTo')
  static BillTo = new EnumValue(1, 'billTo')
}

export class FinancialIntegrationType extends Enum {
  static None = new EnumValue(0, 'none')
  static CsvDownloads = new EnumValue(1, 'csvDownloads')
  static BusinessCentralWithBinaryStream = new EnumValue(2, 'businessCentral')
  static CsvDrop = new EnumValue(3, 'csvDrop')
}

export class AdjustingEntriesFrequency extends Enum {
  static None = new EnumValue(0, 'none')
  static MonthEndAuto = new EnumValue(1, 'monthEndAuto')
  static MonthEndOnDemand = new EnumValue(2, 'monthEndOnDemand')
  static PayPeriodOnDemand = new EnumValue(3, 'payPeriodOnDemand')
}

// Client-only Enumerations
export class VarianceIndicatorLevel extends Enum {
  static None = new EnumValue(0, 'none')
  static Warning = new EnumValue(1, 'warning')
  static Error = new EnumValue(2, 'error')
}

export class UserClaims extends Enum {
  static GlobalAdmin = new EnumValue(0, 'globalAdmin')
  static ContractManager = new EnumValue(1, 'contractManager')
  static ExportManager = new EnumValue(2, 'exportManager')
  static TemplateManager = new EnumValue(3, 'templateManager')
  static ContractReviewer = new EnumValue(4, 'contractReviewer')
  static InventoryManager = new EnumValue(5, 'inventoryManager')
  static AccountManager = new EnumValue(6, 'accountManager')
  static TicketManager = new EnumValue(7, 'ticketManager')
  static SustainabilityAdmin = new EnumValue(8, 'sustainabilityAdmin')
}

export class UserClaimStatus extends Enum {
  static NotSet = new EnumValue(0, 'notSet')
  static Allow = new EnumValue(1, 'allow')
}

export class ModificationExportType extends Enum {
  static LogsTicket = new EnumValue(0, 'logsTicket')
  static ByproductTicket = new EnumValue(1, 'byproductTicket')
  static TransferTicket = new EnumValue(2, 'transferTicket')
  static LogsContract = new EnumValue(3, 'logsContract')
  static ByproductContract = new EnumValue(4, 'byproductContract')
  static TransferContract = new EnumValue(5, 'transferContract')
  static Tract = new EnumValue(6, 'tract')
  static ActivityTemplate = new EnumValue(7, 'activityTemplate')
  static DefectDefinition = new EnumValue(8, 'defectDefinition')
}

export class WidgetType extends Enum {
  static StatsCard = new EnumValueWithIcon(0, 'statsCard', 'mdi-credit-card-chip-outline')
  static BarChart = new EnumValueWithIcon(1, 'barChart', 'mdi-chart-bar')
  static StackedBarChart = new EnumValueWithIcon(2, 'stackedBarChart', 'mdi-chart-bar-stacked')
  static LineChart = new EnumValueWithIcon(3, 'lineChart', 'mdi-chart-line')
}

export class ExportBatchIntegrationStatus extends Enum {
  static Pending = new EnumValue(0, 'pending')
  static Success = new EnumValue(1, 'success')
  static Error = new EnumValue(2, 'error')
  static Retry = new EnumValue(3, 'retry')
  static Interrupted = new EnumValue(4, 'interrupted')
}

export class ExportBatchBiPumpStatus extends Enum {
  static Pending = new EnumValue(0, 'pending')
  static Success = new EnumValue(1, 'success')
  static Error = new EnumValue(2, 'error')
  static Retry = new EnumValue(3, 'retry')
}

export class AdjustingEntriesSnapshotIntegrationStatus extends Enum {
  static Pending = new EnumValue(0, 'pending')
  static Success = new EnumValue(1, 'success')
  static Error = new EnumValue(2, 'error')
  static Retry = new EnumValue(3, 'error')
}

export class AdjustingEntriesSnapshotUploadStatus extends Enum {
  static Unuploaded = new EnumValue(0, 'unuploaded')
  static Uploaded = new EnumValue(1, 'uploaded')
}

export class TransitStatus extends Enum {
  static NotSet = new EnumValueWithDetails(0, 'notSet', { icon: 'mdi-truck', color: 'grey' })
  static Ready = new EnumValueWithDetails(1, 'ready', { icon: 'mdi-truck', color: 'success' })
  static InProgress = new EnumValueWithDetails(2, 'inProgress', { icon: 'mdi-truck', color: 'secondary' })
  static Paused = new EnumValueWithDetails(3, 'paused', { icon: 'truck-pause', color: 'success' })
  static Complete = new EnumValueWithDetails(4, 'complete', { icon: 'mdi-truck-check', color: 'grey' })
  static Reserved = new EnumValueWithDetails(5, 'reserved', { icon: 'truck-account', color: 'success' })
  static Loading = new EnumValueWithDetails(6, 'loading', { icon: 'mdi-truck-cargo-container', color: 'info' })
}

export class VehicleType extends Enum {
  static Trailer = new EnumValue(0, 'trailer')
  static TrainCar = new EnumValue(1, 'trainCar')
}

export class TransporterTicketCategory extends Enum {
  static None = new EnumValue(0, 'none')
  static Logs = new EnumValue(1, 'logs')
  static Byproducts = new EnumValue(2, 'byproduct')
  static All = new EnumValue(50, 'all')
}

export class ContractApprovalStatus extends Enum {
  static Approved = new EnumValue(0, 'approved')
  static PendingModification = new EnumValue(1, 'pendingModification')
  static PendingReview = new EnumValue(2, 'pendingReview')
  static Returned = new EnumValue(3, 'returned')
}

export class NotificationIdentifier extends Enum {
  static NoFiscalYear = new EnumValue(0, 'noFiscalYear')
  static FiscalYearExpiration = new EnumValue(1, 'fiscalYearExpiration')
  static NoPayPeriod = new EnumValue(2, 'noPayPeriod')
  static LastPayPeriod = new EnumValue(3, 'lastPayPeriod')
  static ContractExpiration = new EnumValueWithIcon(4, 'contractExpiration', 'mdi-altimeter')
  static InsuranceExpiration = new EnumValueWithIcon(5, 'insuranceExpiration', 'mdi-shield')
  static DeviceRegistrationExpiration = new EnumValueWithIcon(6, 'deviceRegistrationExpiration', 'mdi-tablet-cellphone')
  static ContractModificationPendingApproval = new EnumValueWithIcon(7, 'contractModificationPendingApproval', 'mdi-file-document-check-outline')
  static ContractModificationReturned = new EnumValueWithIcon(8, 'contractModificationReturned', 'mdi-file-document-check-outline')
  static AccountCertificationExpiration = new EnumValueWithIcon(9, 'accountCertificationExpiration', 'mdi-certificate-outline')
  static AccountEmailInvalid = new EnumValueWithIcon(10, 'accountContactEmailInvalid', 'mdi-email-alert-outline')
  static ConnectionStringError = new EnumValueWithIcon(11, 'connectionStringError', 'mdi-server-network-off')
  static SrcApplicationFailed = new EnumValueWithIcon(12, 'srcApplicationFailed', 'invoice-text-update')
  static DueDiligencePendingApproval = new EnumValueWithIcon(13, 'dueDiligencePendingApproval', 'mdi-clipboard-check-outline')
  static DueDiligenceReturned = new EnumValueWithIcon(14, 'dueDiligenceReturned', 'mdi-clipboard-alert-outline')
  static DueDiligenceApproved = new EnumValueWithIcon(15, 'dueDiligenceApproved', 'mdi-clipboard-check-outline')
}

export class DeckContentType extends Enum {
  static Logs = new EnumValue(0, 'logs')
  static Byproducts = new EnumValue(1, 'byproducts')
}
export class AccountFileCategory extends Enum {
  static Insurance = new EnumValue(0, 'insurance')
  static Certification = new EnumValue(1, 'certification')
}

export class CertificationStatus extends Enum {
  static None = new EnumValue(0, 'none')
  static Inactive = new EnumValue(1, 'inactive')
  static Active = new EnumValue(2, 'active')
  static Expiring = new EnumValue(3, 'expiring')
  static Expired = new EnumValue(4, 'expired')
  static Multiple = new EnumValue(50, 'multiple')

  static isCertified (val) {
    return [this.Active.value, this.Expiring.value, this.Multiple.value].includes(val)
  }
}

export class RecoveryMode extends Enum {
  static None = new EnumValueWithExplanation(0, 'none', undefined)
  static Oldest = new EnumValueWithExplanation(1, 'oldestNonTract', 'oldestAdvanceWithBalanceUnassociatedWithTract')
  static OldestOnTract = new EnumValueWithExplanation(2, 'oldestOnTract', 'oldestAdvanceWithBalanceAssociatedWithContractTract')
  static Specific = new EnumValueWithExplanation(3, 'specific', 'selectASpecificAdvanceFromWhichToRecover')
}

export class MiscPaymentType extends Enum {
  static Advance = new EnumValue(0, 'advance')
  static TractPayment = new EnumValue(1, 'tractPayment')
  static AccountPayment = new EnumValue(2, 'accountPayment')
}

export class SettlementDeliveryMode extends Enum {
  static None = new EnumValue(0, 'none')
  static Email = new EnumValue(1, 'email')
  static Print = new EnumValue(2, 'print')
  static EmailAndPrint = new EnumValue(3, 'emailAndPrint')
}

export class SettlementArchiveStatus extends Enum {
  static None = new EnumValue(0, 'none')
  static Pending = new EnumValue(1, 'pending')
  static DataArchived = new EnumValue(2, 'dataArchvied')
  static Success = new EnumValue(3, 'success')
  static Upgrade = new EnumValue(4, 'upgrade')
  static Error = new EnumValue(5, 'error')
}

export class SettlementEmailType extends Enum {
  static SettlementsByPayee = new EnumValue(0, 'settlementsByPayee')
  static VolumeByPayee = new EnumValue(1, 'volumeByPayee')
}

export class ErrorSource extends Enum {
  static WebClient = new EnumValue(0, 'WC')
  static Server = new EnumValue(1, 'SVR')
  static Unknown = new EnumValue(50, 'UNKNOWN')
}

export class BulkSettlementDeliveryMode extends Enum {
  static SinglePdf = new EnumValue(0, 'singlePdf')
  static ZippedFolder = new EnumValue(1, 'zippedFolder')
  static Email = new EnumValue(2, 'emailPayees')
}

export class HarvestUnits extends Enum {
  static Tons = new EnumValue(0, 'tons')
  static MBF = new EnumValue(1, 'mbf')
  static SpeciesBasedMBF = new EnumValue(2, 'speciesBasedMbf')
}

export class PenaltyType extends Enum {
  static Flat = new EnumValue(0, 'flat')
}

export class TemplateSpecialization extends Enum {
  static None = new EnumValue(0, 'none')
  static Transfer = new EnumValueWithIcon(1, 'transfer', 'mdi-transfer')
  static Penalty = new EnumValueWithIcon(2, 'penalty', 'weight-alert')
  static Depletion = new EnumValueWithIcon(3, 'depletion', 'mdi-wallet-bifold')
  static Obligation = new EnumValueWithIcon(4, 'obligation', 'mdi-tag-multiple-outline')
}

export class TractPayableSpecialization extends Enum {
  static None = new EnumValue(0, 'none')
  static PerformanceBond = new EnumValue(1, 'performanceBond')
  static Cost = new EnumValue(2, 'cost')
  static Adjustment = new EnumValue(3, 'adjustment')
}

export class LiveTicketsType extends Enum {
  static LiveLogsTickets = new EnumValue(0, 'activeLogsTickets')
  static LiveByproductsTickets = new EnumValue(1, 'activeByproductsTickets')
  static LiveTransferTickets = new EnumValue(2, 'activeTransferTickets')
  static LiveLogYardSaleTickets = new EnumValue(3, 'activeLogYardSaleTickets')
}

export class AccountPaymentType extends Enum {
  static OneTime = new EnumValue(0, 'oneTime')
  static Recurring = new EnumValue(1, 'recurring')
  static RecurringAdvance = new EnumValue(2, 'recurringWithAdvance')
}

export class RecoveryStrategy extends Enum {
  static Ticket = new EnumValue(0, 'ticket')
  static RecurringPayment = new EnumValue(1, 'recurringAccountPayment')
}

export class UserStatus extends Enum {
  static Inactive = new EnumValue(0, 'inactive')
  static Active = new EnumValue(1, 'active')
}

export class NotificationScope extends Enum {
  static All = new EnumValue(0, 'all')
  static MyTracts = new EnumValue(1, 'myTracts')
}

export class RolloutStatus extends Enum {
  static NotStarted = new EnumValue(0, 'notStarted')
  static Active = new EnumValue(1, 'active')
  static Complete = new EnumValue(2, 'complete')
}

export class DaemonAppIdentifier extends Enum {
  static Loader = new EnumValue(0, 'loader')
  static Transporter = new EnumValue(1, 'transporter')
  static Unknown = new EnumValue(50, 'unknown')
}

export class SamsaraIntegrationRecordStatus extends Enum {
  static New = new EnumValue(0, 'new')
  static Success = new EnumValue(1, 'success')
  static Failure = new EnumValue(2, 'failure')
}

export class SamsaraIntegrationType extends Enum {
  static RefreshTicketStatusAttributes = new EnumValue(0, 'refreshAttributes')
  static CreateProductTag = new EnumValue(1, 'createProductTag')
  static CreateDestinationTag = new EnumValue(2, 'createDestinationTag')
  static UpdateTrailer = new EnumValue(3, 'updateTrailer')
}

export class SamsaraIntegrationFailureReason extends Enum {
  static BadToken = new EnumValue(0, 'badApiToken')
  static MissingTag = new EnumValue(1, 'missingTag')
  static MissingAttribute = new EnumValue(2, 'missingAttribute')
  static Unknown = new EnumValue(50, 'unknown')
}

export class SamsaraIntegrationStatus extends Enum {
  static NotConfigured = new EnumValue(0, 'notAvailable')
  static Active = new EnumValue(1, 'active')
  static Paused = new EnumValue(2, 'paused')
  static Interrupted = new EnumValue(3, 'interrupted')
  static Pending = new EnumValue(4, 'pending')
}

export class SamsaraIntegrationStatusReason extends Enum {
  static None = new EnumValue(0, 'none')
  static Unauthorized = new EnumValue(1, 'unauthorized')
  static Unknown = new EnumValue(50, 'unknown')
}

export class SettlementVerificationType extends Enum {
  static Logs = new EnumValue(0, 'logs')
  static Byproducts = new EnumValue(1, 'byproducts')
  static LogsAccruals = new EnumValue(2, 'logsAccruals')
}

export class SettlementVerificationStatus extends Enum {
  static Verified = new EnumValue(0, 'verified')
  static Invalidated = new EnumValue(1, 'invalidated')
}

export class SettlementInvalidatedReason extends Enum {
  static None = new EnumValue(0, 'invalidSettlementReasonNone')
  static TicketsPosted = new EnumValue(1, 'invalidSettlementReasonTicketsPosted')
  static TicketsUnposted = new EnumValue(2, 'invalidSettlementReasonTicketsUnposted')
  static TractPayment = new EnumValue(3, 'invalidSettlementReasonTractPayment')
  static AccountPayment = new EnumValue(4, 'invalidSettlementReasonAccountPayment')
  static Advance = new EnumValue(5, 'invalidSettlementReasonAdvance')
  static AdvanceModification = new EnumValue(6, 'invalidSettlementReasonAdvanceModification')
  static TicketCorrection = new EnumValue(7, 'invalidSettlementReasonTicketCorrection')
  static AdvanceRecovery = new EnumValue(8, 'invalidSettlementReasonAdvanceRecovery')
}

export class EmailStatus extends Enum {
  static Active = new EnumValue(0, 'active')
  static Invalid = new EnumValue(1, 'invalid')
}

export class EmailSuppressionType extends Enum {
  static None = new EnumValue(0, 'none')
  static Bounce = new EnumValue(1, 'bouncedEmail')
  static Block = new EnumValue(2, 'blockedEmail')
  static Invalid = new EnumValue(3, 'invalidEmailAddress')
  static Spam = new EnumValue(4, 'spamReport')
}

export class TicketStatusReason extends Enum {
  static MissingDeck = new EnumValueWithIcon(0, 'missingDeck', dockageDeck.path)
  static MissingDriver = new EnumValueWithIcon(1, 'missingDriver', truckAccount.path)
  static MissingScaleData = new EnumValueWithIcon(2, 'missingScaleData', 'mdi-ruler-square')

  static getReasonIcon (reason) {
    switch (reason) {
      case this.MissingDeck.value:
        return dockageDeck.path
      case this.MissingDriver.value:
        return truckAccount.path
      case this.MissingScaleData.value:
        return 'mdi-ruler-square'
      default:
        return 'mdi-exception'
    }
  }
}

export class TractTypeSpecialization extends Enum {
  static None = new EnumValueWithIcon(0, 'none')
  static Depletion = new EnumValueWithIcon(1, 'depletion', 'mdi-wallet-bifold')
  static ObligationUncapped = new EnumValueWithIcon(2, 'obligationUncapped', 'mdi-tag-multiple-outline')
  static ObligationCapped = new EnumValueWithIcon(3, 'obligationCapped', 'mdi-tag-multiple')
}

export class TractRequirementType extends Enum {
  static Create = new EnumValue(0, 'tractCreation')
  static AddContract = new EnumValue(1, 'addingAContract')
}

const allTractTypeCategories = [TractTypeCategory.Stumpage.value, TractTypeCategory.Delivered.value]
const stumpageOnly = [TractTypeCategory.Stumpage.value]
const deliveredOnly = [TractTypeCategory.Delivered.value]
const allTractRequirementTypes = [TractRequirementType.Create.value, TractRequirementType.AddContract.value]
const addContractOnly = [TractRequirementType.AddContract.value]
const allTractTypeSpecializations = TractTypeSpecialization.enums.map(s => s.value)
const noSpecializationOnly = [TractTypeSpecialization.None.value]

export class TractRequiredField extends Enum {
  static Forester = new EnumValueWithDetails(0, 'forester', { categories: allTractTypeCategories, requirementTypes: allTractRequirementTypes, specializations: allTractTypeSpecializations })
  static LoggerAccount = new EnumValueWithDetails(1, 'defaultLogger', { categories: allTractTypeCategories, requirementTypes: allTractRequirementTypes, specializations: allTractTypeSpecializations })
  static SupplierAccount = new EnumValueWithDetails(2, 'supplier', { categories: allTractTypeCategories, requirementTypes: allTractRequirementTypes, specializations: allTractTypeSpecializations })
  static HaulerAccount = new EnumValueWithDetails(3, 'hauler', { categories: allTractTypeCategories, requirementTypes: allTractRequirementTypes, specializations: allTractTypeSpecializations })
  static ConsultingForesterAccount = new EnumValueWithDetails(4, 'consultingForester', { categories: allTractTypeCategories, requirementTypes: allTractRequirementTypes, specializations: allTractTypeSpecializations })
  static Team = new EnumValueWithDetails(5, 'team', { categories: allTractTypeCategories, requirementTypes: allTractRequirementTypes, specializations: allTractTypeSpecializations })
  static HarvestType = new EnumValueWithDetails(6, 'harvestType', { categories: allTractTypeCategories, requirementTypes: allTractRequirementTypes, specializations: allTractTypeSpecializations })
  static LoggingMethod = new EnumValueWithDetails(7, 'loggingMethod', { categories: allTractTypeCategories, requirementTypes: allTractRequirementTypes, specializations: allTractTypeSpecializations })
  static TimberCategory = new EnumValueWithDetails(8, 'timberCategory', { categories: deliveredOnly, requirementTypes: allTractRequirementTypes, specializations: allTractTypeSpecializations })
  static Conversion = new EnumValueWithDetails(9, 'conversion', { categories: deliveredOnly, requirementTypes: allTractRequirementTypes, specializations: allTractTypeSpecializations })
  static LoggingAccessibilityTag = new EnumValueWithDetails(10, 'loggingAccessibilityTag', { categories: allTractTypeCategories, requirementTypes: allTractRequirementTypes, specializations: allTractTypeSpecializations })
  static MapAndParcelNumber = new EnumValueWithDetails(11, 'mapAndParcelNumber', { categories: allTractTypeCategories, requirementTypes: allTractRequirementTypes, specializations: allTractTypeSpecializations })
  static Acres = new EnumValueWithDetails(12, 'acres', { categories: allTractTypeCategories, requirementTypes: allTractRequirementTypes, specializations: allTractTypeSpecializations })
  static Cost = new EnumValueWithDetails(13, 'cost', { categories: allTractTypeCategories, requirementTypes: allTractRequirementTypes, specializations: noSpecializationOnly })
  static HarvestStartDate = new EnumValueWithDetails(14, 'harvestStartDate', { categories: allTractTypeCategories, requirementTypes: allTractRequirementTypes, specializations: allTractTypeSpecializations })
  static HarvestEndDate = new EnumValueWithDetails(15, 'harvestEndDate', { categories: allTractTypeCategories, requirementTypes: allTractRequirementTypes, specializations: allTractTypeSpecializations })
  static AvailabilityStartDate = new EnumValueWithDetails(16, 'availabilityStart', { categories: allTractTypeCategories, requirementTypes: allTractRequirementTypes, specializations: allTractTypeSpecializations })
  static AvailabilityEndDate = new EnumValueWithDetails(17, 'availabilityEnd', { categories: allTractTypeCategories, requirementTypes: allTractRequirementTypes, specializations: allTractTypeSpecializations })
  static PurchaseDate = new EnumValueWithDetails(18, 'purchaseDate', { categories: stumpageOnly, requirementTypes: allTractRequirementTypes, specializations: allTractTypeSpecializations })
  static ScalingRegion = new EnumValueWithDetails(19, 'scalingRegion', { categories: allTractTypeCategories, requirementTypes: allTractRequirementTypes, specializations: allTractTypeSpecializations })
  static Loggers = new EnumValueWithDetails(20, 'loggers', { categories: allTractTypeCategories, requirementTypes: allTractRequirementTypes, specializations: allTractTypeSpecializations })
  static Landowners = new EnumValueWithDetails(21, 'landowners', { categories: allTractTypeCategories, requirementTypes: allTractRequirementTypes, specializations: allTractTypeSpecializations })
  static PermitNumbers = new EnumValueWithDetails(22, 'permitNumbers', { categories: allTractTypeCategories, requirementTypes: allTractRequirementTypes, specializations: allTractTypeSpecializations })
  static Certifications = new EnumValueWithDetails(23, 'certifications', { categories: allTractTypeCategories, requirementTypes: allTractRequirementTypes, specializations: allTractTypeSpecializations })
  static TractContents = new EnumValueWithDetails(24, 'cruiseData', { categories: allTractTypeCategories, requirementTypes: addContractOnly, specializations: allTractTypeSpecializations })
}

export class NamedRateSpecialization extends Enum {
  static None = new EnumValueWithIcon(0, 'none')
  static Obligation = new EnumValue(1, 'obligation')
}

export class DeliveryFormat extends Enum {
  static None = new EnumValue(0, 'none')
  static Pdf = new EnumValue(1, 'pdf')
  static Csv = new EnumValue(2, 'csv')
  static All = new EnumValue(50, 'both') // labeled 'both' because we only have 2 file types at the moment
}

export class DailyScaleDataFormat extends Enum {
  static None = new EnumValue(0, 'none')
  static Csv = new EnumValue(1, 'csv')
}

export class GatePassType extends Enum {
  static Departure = new EnumValue(0, 'departure')
  static Pickup = new EnumValue(1, 'pickup')
}

export class ScalingRegion extends Enum {
  static NotDefined = new EnumValue(0, 'notDefined')
  static Region1 = new EnumValue(1, 'regionOne')
  static Region4 = new EnumValue(2, 'regionFour')
  static Region5 = new EnumValue(3, 'regionFive')
  static RegionE6 = new EnumValue(4, 'regionESix')
  static RegionW6 = new EnumValue(5, 'regionWSix')
}

export class ScalingDataImportProgress extends Enum {
  static InProgress = new EnumValue(0, 'inProgress')
  static Success = new EnumValue(1, 'success')
  static PartialSuccess = new EnumValue(2, 'partialSuccess')
  static Failed = new EnumValue(3, 'failed')
}

export class ScalingStatus extends Enum {
  static NotSet = new EnumValueWithDetails(0, 'notSet',
    {
      icon: 'mdi-ruler-square',
      color: 'grey'
    })

  static Ready = new EnumValueWithDetails(1, 'ready',
    {
      icon: 'mdi-ruler-square',
      color: 'success'
    })

  static InProgress = new EnumValueWithDetails(2, 'inProgress',
    {
      icon: 'mdi-ruler-square',
      color: 'secondary'
    })

  static Complete = new EnumValueWithDetails(3, 'complete',
    {
      icon: 'ruler-square-check',
      color: 'grey'
    })

  static Expansion = new EnumValueWithDetails(4, 'expansion',
    {
      icon: 'mdi-alpha-e-circle-outline',
      color: 'grey'
    })

  static Recompute = new EnumValueWithDetails(5, 'recompute',
    {
      icon: 'mdi-alpha-r-circle-outline',
      color: 'grey'
    })

  static Importing = new EnumValueWithDetails(6, 'importing',
    {
      icon: 'mdi-alpha-i-circle-outline',
      color: 'grey'
    })
}

export class VolumePricingMode extends Enum {
  static NotSet = new EnumValue(0, 'notSet')
  static WholeLengthTopDiameter = new EnumValue(1, 'wholeLengthTopDiameter')
  static SegmentLengthSegmentTop = new EnumValue(2, 'segmentLengthSegmentTop')
  static WholeLengthSegmentDiameter = new EnumValue(3, 'wholeLengthSegmentDiameter')
}

export class ScaleDataSource extends Enum {
  static Scaled = new EnumValue(0, 'scaled')
  static Expansion = new EnumValue(1, 'expansion')
  static Imported = new EnumValue(2, 'imported')
}

export class ScaleSheetImportFileType extends Enum {
  static Csv = new EnumValue(0, 'csv')
  static Delimited = new EnumValue(1, 'delimited')
  static FixedLength = new EnumValue(2, 'fixedLength')
}

export class SettlementCsvExportType extends Enum {
  static AccountsPayable = new EnumValueWithDetails(0, 'accountsPayable', { requestType: 'apcsvexport' })
  static AccountsReceivable = new EnumValueWithDetails(1, 'accountsReceivable', { requestType: 'arcsvexport' })
  static Accruals = new EnumValueWithDetails(2, 'accruals', { requestType: 'jecsvexport' })
  static StatisticalJournals = new EnumValueWithDetails(3, 'statisticalJournals', { requestType: 'sjcsvexport' })
  static All = new EnumValueWithDetails(4, 'all', { requestType: null })
}

export class ScheduledRateChangeStatus extends Enum {
  static Pending = new EnumValue(0, 'pending')
  static Applied = new EnumValue(1, 'applied')
  static Failed = new EnumValue(2, 'failed')
}

export class ScheduledRateChangeStatusReason extends Enum {
  static Unknown = new EnumValueWithExplanation(0, 'unknown', 'srcApplicationFailedReasonUnknown')
  static Expansion = new EnumValueWithExplanation(1, 'expansion', 'srcApplicationFailedReasonExpansion')
}

export class YardInstructionStatus extends Enum {
  static Active = new EnumValue(0, 'active')
  static Paused = new EnumValue(1, 'paused')
}

export class LicenseType extends Enum {
  static ClassA = new EnumValue(0, 'classA')
  static ClassB = new EnumValue(1, 'classB')
  static ClassC = new EnumValue(2, 'classC')
  static ClassD = new EnumValue(3, 'classD')
  static ClassM = new EnumValue(4, 'classM')
}

export class LicenseStatus extends Enum {
  static Active = new EnumValue(0, 'active')
  static Expired = new EnumValue(1, 'expired')
  static Suspended = new EnumValue(2, 'suspended')
  static Revoked = new EnumValue(3, 'revoked')
}

export class StorageReplicationMode extends Enum {
  static None = new EnumValue(0, 'none')
  static TableStorage = new EnumValue(1, 'tableStorage')
  static BlobStorage = new EnumValue(2, 'blobStorage')
}

export class SettlementEmailMessageMode extends Enum {
  static None = new EnumValue(0, 'none')
  static Default = new EnumValue(1, 'default')
}

export class EndangeredSpeciesMode extends Enum {
  static None = new EnumValue(0, 'none')
  static Manual = new EnumValue(1, 'manual')
  static SpeciesLayer = new EnumValue(2, 'speciesLayer')
}

export class DueDiligenceApprovalMode extends Enum {
  static None = new EnumValueWithExplanation(0, 'none', undefined)
  static Review = new EnumValueWithExplanation(1, 'review', 'dueDiligenceApprovalModeReviewInfo')
  static Strict = new EnumValueWithExplanation(2, 'strict', 'dueDiligenceApprovalModeStrictInfo')
}

export class SettlementReportMode extends Enum {
  static Standard = new EnumValue(0, 'standard')
  static Brief = new EnumValue(1, 'brief')
}

export class BidCostTypeMode extends Enum {
  static Strict = new EnumValue(0, 'strict')
  static Mixed = new EnumValue(1, 'mixed')
}

export class TractHudSection extends Enum {
  static TractType = new EnumValueWithIcon(0, 'tractType', IconHelpers.getIconForObjectType('tractType'))
  static TractTypeCategory = new EnumValueWithIcon(1, 'category', 'mdi-shape-outline')
  static LoggingAccessibility = new EnumValueWithIcon(2, 'loggingAccessibility', 'mdi-road-variant')
  static Rainfall = new EnumValueWithIcon(3, 'precipitation', 'mdi-weather-pouring')
  static HarvestDates = new EnumValueWithIcon(4, 'harvestPeriod', 'mdi-calendar-range')
  static HarvestProgress = new EnumValueWithIcon(5, 'harvestProgress', 'mdi-progress-check')

  static defaultLayout () {
    return this.enums.map(section => ({ section: section.value, isVisible: true }))
  }

  static sortedByPriority (sections) {
    return [...sections].sort((a, b) => a.displayPriority - b.displayPriority)
  }
}

// Whether a mitigation item's habitat/risk was found in the harvest area. NotSet = not yet addressed.
export class RiskPresence extends Enum {
  static NotSet = new EnumValue(0, 'notSet')
  static Present = new EnumValue(1, 'present')
  static NotPresent = new EnumValue(2, 'notPresent')
}

export class ConversionImpact extends Enum {
  static None = new EnumValue(0, 'none')
  static Low = new EnumValue(1, 'low')
  static Medium = new EnumValue(2, 'medium')
  static High = new EnumValue(3, 'high')
}

// One row per submit→decide due diligence cycle. Returned = rejected-for-rework (the UI labels it "Rejected").
export class DueDiligenceApprovalStatus extends Enum {
  static PendingReview = new EnumValue(0, 'pendingReview')
  static Approved = new EnumValue(1, 'approved')
  static Returned = new EnumValue(2, 'rejected')
}

export class ConnectionStringStatus extends Enum {
  static Pending = new EnumValue(0, 'pending')
  static Active = new EnumValue(1, 'active')
  static Error = new EnumValue(2, 'error')
}

export class CustomConnectionType extends Enum {
  static BiReplication = new EnumValue(0, 'blobStorage')
  static CustomMapLayers = new EnumValue(1, 'customLayers')
}

export class ScaleSheetTicketType extends Enum {
  static IncludesVolume = new EnumValueWithDetails(0, 'includesVolume',
    {
      scalingStatus: ScalingStatus.Importing.value
    })

  static Expanded = new EnumValueWithDetails(1, 'expanded',
    {
      scalingStatus: ScalingStatus.Expansion.value
    })

  static WeightBased = new EnumValueWithDetails(2, 'weightBased',
    {
      scalingStatus: undefined
    })
}

export class ImportTabStatus extends Enum {
  static Success = new EnumValue(0, 'success')
  static Skipped = new EnumValue(1, 'skipped')
  static Failed = new EnumValue(2, 'failed')
  static SkippedPrerequisite = new EnumValue(3, 'skippedPrerequisite')
}
